import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useClientOnboardingStore } from "@/store/clientOnboardingStore";
import { useCoverageStore } from "@/store/coverageStore/coverageStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { isAppLevelUser } from "@/utils/isPlatformView";
import { Box, Card, Flex, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import {
  IconArrowNarrowRight,
  IconBriefcase,
  IconBuildingCommunity,
  IconMapPin,
  IconPlus,
  IconUserHexagon,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import styles from "./dashboard.module.scss";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useInsideEntity } from "@/hooks/usePlatformView";

export const Route = createFileRoute("/_authenticated/")({
  component: Dashboard,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function Dashboard() {
  const navigate = useNavigate();
  const { setClientId } = useClientOnboardingStore();
  const { resetCoverageStore } = useCoverageStore();

  useBreadcrumb([
    {
      title: "Home",
      href: "/",
      icon: getRouteIcon(""),
    },
  ]);

  const { isMainAppLogin, isEntityLogin, isAppLevel, allEntities, userType } = useActiveSession();
  const insideEntity = useInsideEntity();
  const appLevelUser = isAppLevelUser({ isAppLevel, allEntities: allEntities ?? false, userType });
  // SERV-3232: clients and locations live inside an entity, so their entry points follow the
  // MAIN_APP_HIDDEN_ROUTES rule: hidden at ServEngine level, open to an app-level user once she
  // picks one of her entities.
  const entityScopedEntriesOpen = !isMainAppLogin || (appLevelUser && insideEntity);
  const { hasAccess } = usePermissions();
  const { create: canCreateClients, view: canViewClients } = hasAccess({
    module: MODULE_KEYS_MAP.clients,
  });
  const { create: canCreateLocations, view: canViewLocations } = hasAccess({
    module: MODULE_KEYS_MAP.locations,
  });
  const { create: canCreateWorkOrders, view: canViewWorkOrders } = hasAccess({
    module: MODULE_KEYS_MAP.workOrders,
  });
  const { create: canCreateAssets } = hasAccess({
    module: MODULE_KEYS_MAP.assets,
  });
  const { view: canViewServiceProviders } = hasAccess({
    module: MODULE_KEYS_MAP.serviceProviders,
  });

  const workspaceActions = [
    canCreateClients && entityScopedEntriesOpen
      ? {
          label: "New Client",
          to: "/clients/add-new-client/$stepId",
          onClick: () => {
            setClientId("");
            resetCoverageStore();
            localStorage.removeItem("client-onboarding-storage");
            navigate({
              to: "/clients/add-new-client/$stepId",
              params: { stepId: "basic-info" },
            });
          },
        }
      : null,
    canCreateLocations && entityScopedEntriesOpen
      ? { label: "New location", to: "/locations" }
      : null,
    canCreateWorkOrders ? { label: "New Work Order", to: "/work-orders/add-work-order" } : null,
    canCreateAssets && !isMainAppLogin && !isEntityLogin
      ? { label: "New Asset", to: "/assets" }
      : null,
  ];

  const modules = [
    // SERV-3225: Clients is reachable at ServEngine level now; New Client above
    // stays hidden there because a client has to be created inside an entity.
    canViewClients ? { label: "Clients", to: "/clients", icon: IconBuildingCommunity } : null,
    canViewLocations && entityScopedEntriesOpen
      ? { label: "Locations", to: "/locations", icon: IconMapPin }
      : null,
    canViewServiceProviders && !isEntityLogin
      ? { label: "Service Provider", to: "/service-providers", icon: IconUserHexagon }
      : null,
    canViewWorkOrders ? { label: "Work Order", to: "/work-orders", icon: IconBriefcase } : null,
  ];

  const hasWorkspaceActions =
    canCreateClients || canCreateLocations || canCreateWorkOrders || canCreateAssets;

  const hasModules =
    canViewClients || canViewLocations || canViewServiceProviders || canViewWorkOrders;

  return (
    <Flex direction="column" className={styles.container}>
      <Stack gap="xl">
        {hasWorkspaceActions ? (
          <Box component="section" className={styles.section}>
            <Stack gap="xs" className={styles.sectionHeader}>
              <Title order={3} className={styles.sectionTitle}>
                Workspace
              </Title>
              <Text className={styles.sectionSubtitle}>What will you create today</Text>
            </Stack>

            <SimpleGrid
              cols={{ base: 1, sm: 2, lg: 4 }}
              spacing="md"
              className={styles.workspaceGrid}
            >
              {workspaceActions.map((action) =>
                action ? (
                  <Card
                    key={action?.label}
                    radius="md"
                    withBorder
                    shadow="xs"
                    className={styles.workspaceCard}
                    onClick={() =>
                      action?.onClick
                        ? action.onClick()
                        : action?.to
                          ? navigate({ to: action.to })
                          : null
                    }
                  >
                    <Button
                      variant="secondary"
                      size="large"
                      iconLeft={<IconPlus size={18} />}
                      style={{ width: "100%" }}
                      className={styles.workspaceButton}
                    >
                      {action?.label}
                    </Button>
                  </Card>
                ) : null,
              )}
            </SimpleGrid>
          </Box>
        ) : null}

        {hasModules ? (
          <Box component="section" className={styles.section}>
            <Stack gap="xs" className={styles.sectionHeader}>
              <Title order={3} className={styles.sectionTitle}>
                Modules
              </Title>
              <Text className={styles.sectionSubtitle}>What do you want to look at today</Text>
            </Stack>

            <SimpleGrid
              cols={{ base: 1, sm: 2, lg: 4 }}
              spacing="md"
              className={styles.modulesGrid}
            >
              {modules.map((module) => {
                const Icon = module?.icon ?? null;
                return module ? (
                  <Card
                    key={module?.label}
                    radius="md"
                    withBorder
                    shadow="xs"
                    className={styles.moduleCard}
                    onClick={() => navigate({ to: module?.to })}
                  >
                    <Box className={styles.moduleIconWrapper}>
                      {Icon ? (
                        <Icon
                          size={16}
                          className={styles.moduleIcon}
                          color="var(--icon-brand-strong)"
                        />
                      ) : null}
                    </Box>
                    <Box className={styles.moduleTextWrapper}>
                      <Text className={styles.moduleTitle}>{module?.label}</Text>
                      <IconArrowNarrowRight size={20} className={styles.moduleChevron} />
                    </Box>
                    <Box className={styles.moduleSubtitleWrapper}>
                      <Text className={styles.moduleSubtitle}>0 items</Text>
                    </Box>
                  </Card>
                ) : null;
              })}
            </SimpleGrid>
          </Box>
        ) : null}
      </Stack>
    </Flex>
  );
}
